import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit, computed, inject } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';

import { ScoreboardSubscriptionService } from '../../../../event-subscription/scoreboard-subscription.service';
import { MyBetsBet, MyBetsListContext } from '../../../../my-bets-base/models/my-bets-viewmodels';
import { ScoreboardViewmodelFactory } from '../../../../scoreboards/models/scoreboard-viewmodel.factory';
import { ScoreboardViewModel } from '../../../../scoreboards/models/scoreboard.viewmodel';
import { MyBetsEventModel } from '../../../models/my-bets-event.model';
import { MyBetsCondensedScoreboardService } from '../../../services/my-bets-condensed-scoreboard.service';
import { MyBetsVisualizationService } from '../../../services/my-bets-visualization.service';
import { MyBetsScoreboardModule } from '../my-bets-scoreboard.module';

@Component({
    imports: [MyBetsScoreboardModule, NgClass, NgTemplateOutlet],
    selector: 'ms-my-bets-condensed-scoreboard',
    templateUrl: 'my-bets-condensed-scoreboard.component.html',
    styleUrls: ['./my-bets-condensed-scoreboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsCondensedScoreboardComponent implements OnDestroy, OnInit {
    sitecore = inject(Sitecore);
    private epcotConfig = inject(EpcotConfig);
    private pushService = inject<ScoreboardSubscriptionService<MyBetsEventModel>>(ScoreboardSubscriptionService);
    private viewModelFactory = inject(ScoreboardViewmodelFactory);
    private changeDetector = inject(ChangeDetectorRef);
    private myBetsVisualizationService = inject(MyBetsVisualizationService);
    private myBetsCondensedScoreboardService = inject(MyBetsCondensedScoreboardService);

    @HostBinding('class') get className() {
        return 'live-bets-condensed';
    }

    @Input() bet: MyBetsBet;
    @Input() myBetsListContext: MyBetsListContext;

    @Input() event: MyBetsEventModel;

    pushSubscription: EventSubscription<MyBetsEventModel>;
    baseScoreboardViewModel: ScoreboardViewModel;
    isEpcotEnabled = this.epcotConfig.isEnabled;
    liveText = this.sitecore.event.Live;
    isLive = computed(() => this.event.scoreboard.started);
    showLiveIndicator = computed(() => this.isLive() && !this.event.scoreboard?.isFinished);
    showAwayIndicator = computed(() => this.isEpcotEnabled || !this.isLive());
    awayIndicator: string | undefined;
    totalsScore: string | undefined;
    homePlayer: string | undefined;
    awayPlayer: string | undefined;

    constructor(epcotConfig: EpcotConfig) {}

    ngOnInit(): void {
        this.loadScoreboard();
    }

    private initContainer(): void {
        this.baseScoreboardViewModel = this.viewModelFactory.createMyBetsScoreBoardViewModel(this.event);
        if (!this.baseScoreboardViewModel) {
            return;
        }

        this.totalsScore = this.myBetsCondensedScoreboardService.getTotalsScore(this.event, this.baseScoreboardViewModel);

        const { participant1, participant2 } = this.myBetsVisualizationService.getParticipants(this.event.participants);
        this.homePlayer = participant1;
        this.awayPlayer = participant2;
        this.awayIndicator = this.event.viewType === FixtureViewType.American ? this.sitecore.globalMessages.AwayIndicator : '-';
    }

    private loadScoreboard(): void {
        this.initContainer();
        this.subscribe();
    }

    protected onScoreboardUpdatedInternal = (): void => {
        if (!this.baseScoreboardViewModel) {
            this.initContainer();
        } else {
            this.baseScoreboardViewModel.updateFromScoreboard(this.event.scoreboard);
        }
        this.totalsScore = this.myBetsCondensedScoreboardService.getTotalsScore(this.event, this.baseScoreboardViewModel);
        this.changeDetector.markForCheck();
    };

    private subscribe(): void {
        if (this.event) {
            this.pushSubscription = this.pushService.subscribe([{ event: this.event, callback: this.onScoreboardUpdatedInternal }]);
        }
    }

    ngOnDestroy(): void {
        this.pushSubscription?.unsubscribe();
    }
}
